// Piezas serializadas que la entrega tiene que identificar antes de cerrar la venta, y el body de
// complete-sale que sale de lo capturado. Puro, sin React.
import { CATEGORY_LABEL } from './cartLines';
import type {
    CompleteSaleLine,
    PinPiecesLine,
    ReservedLine,
    TrackingMode,
    WizardSession,
} from './types';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';

export const IMEI_LENGTH = 15;

/** Modos que piden un identificador por pieza. */
export type SerializedMode = Exclude<TrackingMode, 'QUANTITY'>;

/** Una pieza por capturar: su línea, el SKU que se vende (el componente si es kit) y su lugar. */
export interface DeliveryPiece {
    key: string;
    lineId: string;
    sku: string;
    trackingMode: SerializedMode;
    label: string;
}

/** Lo capturado por pieza, indexado por `DeliveryPiece.key`. */
export type CapturedIdentifiers = Record<string, string>;

/** Teléfono que se enrola: la línea financiada y el SKU de su teléfono. */
export interface FinancedPhone {
    lineId: string;
    sku: string;
}

interface LineUnit {
    sku: string;
    qty: number;
    trackingMode: TrackingMode;
    name: string;
}

function lineName(line: ReservedLine): string {
    return itemDisplayName({ sku: line.sku, ...line.product });
}

/** El kit se parte en sus componentes; el producto suelto es una sola unidad. */
function unitsOf(line: ReservedLine): LineUnit[] {
    const name = lineName(line);
    if (line.components.length > 0) {
        return line.components.map((c) => ({
            sku: c.sku,
            qty: c.qty,
            trackingMode: c.trackingMode,
            name: `${name} · ${CATEGORY_LABEL[c.category]}`,
        }));
    }
    return [
        {
            sku: line.sku,
            qty: line.qty,
            trackingMode: line.product?.trackingMode ?? 'QUANTITY',
            name,
        },
    ];
}

/** SKU del teléfono de la línea: el suyo si es teléfono, el del componente si es kit. */
export function phoneSkuOf(line: ReservedLine): string {
    if (line.product?.category === 'PHONE') return line.sku;
    return line.components.find((c) => c.category === 'PHONE')?.sku ?? line.sku;
}

/** Línea financiada de la venta a crédito; `undefined` en sesiones sin líneas. */
export function financedPhoneOf(session: WizardSession | undefined): FinancedPhone | undefined {
    const lines = session?.reservedLines;
    if (!lines) return undefined;
    const line =
        lines.find((l) => l.lineId === session.financedLineId) ??
        lines.find((l) => l.paymentType === 'CREDIT');
    return line ? { lineId: line.lineId, sku: phoneSkuOf(line) } : undefined;
}

function isPinned(line: ReservedLine, unit: LineUnit): boolean {
    const pinned = line.identifiers.find((i) => i.sku === unit.sku)?.identifiers ?? [];
    return pinned.length >= unit.qty;
}

/**
 * Piezas por identificar de las líneas apartadas. Se omiten las ya fijadas en la sesión y, en
 * crédito, el teléfono financiado: su IMEI se elige al enrolar (D-07).
 */
export function piecesToCapture(
    session: WizardSession | undefined,
    financed?: FinancedPhone,
): DeliveryPiece[] {
    const lines = (session?.reservedLines ?? []).filter((l) => l.status === 'RESERVED');
    return lines.flatMap((line) =>
        unitsOf(line)
            .filter((u) => u.trackingMode !== 'QUANTITY' && !isPinned(line, u))
            .filter((u) => !(financed?.lineId === line.lineId && financed.sku === u.sku))
            .flatMap((u) =>
                Array.from({ length: u.qty }, (_, i) => ({
                    key: `${line.lineId}|${u.sku}|${String(i)}`,
                    lineId: line.lineId,
                    sku: u.sku,
                    trackingMode: u.trackingMode as SerializedMode,
                    label:
                        u.qty > 1
                            ? `${u.name} · pieza ${String(i + 1)} de ${String(u.qty)}`
                            : u.name,
                })),
            ),
    );
}

/** Deja solo lo que el modo admite: 15 dígitos para IMEI, sin espacios para serie. */
export function normalizeIdentifier(mode: SerializedMode, raw: string): string {
    if (mode === 'IMEI') return raw.replace(/\D/g, '').slice(0, IMEI_LENGTH);
    return raw.replace(/\s/g, '');
}

export function isIdentifierComplete(mode: SerializedMode, value: string): boolean {
    return mode === 'IMEI' ? value.length === IMEI_LENGTH : value.length > 0;
}

/** Piezas con el mismo identificador que otra pieza del mismo SKU. */
export function duplicatedPieceKeys(
    pieces: DeliveryPiece[],
    captured: CapturedIdentifiers,
): Set<string> {
    const seen = new Map<string, string>();
    const duplicated = new Set<string>();
    for (const piece of pieces) {
        const value = captured[piece.key] ?? '';
        if (value === '') continue;
        const id = `${piece.sku}|${value}`;
        const first = seen.get(id);
        if (first === undefined) {
            seen.set(id, piece.key);
        } else {
            duplicated.add(first);
            duplicated.add(piece.key);
        }
    }
    return duplicated;
}

/** Todas las piezas tienen identificador completo y ninguno se repite. */
export function allPiecesCaptured(pieces: DeliveryPiece[], captured: CapturedIdentifiers): boolean {
    return (
        pieces.every((p) => isIdentifierComplete(p.trackingMode, captured[p.key] ?? '')) &&
        duplicatedPieceKeys(pieces, captured).size === 0
    );
}

/** Body de complete-sale; `undefined` si no hay piezas que mandar (líneas por cantidad o sesión vieja). */
export function completeSaleLinesOf(
    pieces: DeliveryPiece[],
    captured: CapturedIdentifiers,
): CompleteSaleLine[] | undefined {
    const byLine = new Map<string, Map<string, string[]>>();
    for (const piece of pieces) {
        const skus = byLine.get(piece.lineId) ?? new Map<string, string[]>();
        skus.set(piece.sku, [...(skus.get(piece.sku) ?? []), captured[piece.key] ?? '']);
        byLine.set(piece.lineId, skus);
    }
    if (byLine.size === 0) return undefined;
    return [...byLine].map(([lineId, skus]) => ({
        lineId,
        components: [...skus].map(([sku, identifiers]) => ({ sku, identifiers })),
    }));
}

/** Piezas de la línea y SKU que el silo rechazó. */
export function pieceKeysOf(
    pieces: DeliveryPiece[],
    target: { lineId: string; sku: string },
): string[] {
    return pieces
        .filter((p) => p.lineId === target.lineId && p.sku === target.sku)
        .map((p) => p.key);
}

const FINANCED_KEY = 'financed-phone';

function financedLineOf(session: WizardSession | undefined): ReservedLine | undefined {
    const financed = financedPhoneOf(session);
    return session?.reservedLines?.find((l) => l.lineId === financed?.lineId);
}

/** Nombre del equipo financiado; «Equipo» en sesiones sin líneas. */
export function financedPhoneName(session: WizardSession | undefined): string {
    const line = financedLineOf(session);
    return line?.product ? lineName(line) : 'Equipo';
}

/** Teléfono de la línea financiada que se rastrea por IMEI, o `undefined`. */
function financedImeiUnit(line: ReservedLine | undefined): LineUnit | undefined {
    if (!line) return undefined;
    const phone = unitsOf(line).find((u) => u.sku === phoneSkuOf(line));
    return phone?.trackingMode === 'IMEI' ? phone : undefined;
}

/** El contrato lleva el IMEI: la venta tiene un teléfono financiado rastreado por IMEI. */
export function financedImeiRequired(session: WizardSession | undefined): boolean {
    return financedImeiUnit(financedLineOf(session)) !== undefined;
}

/** Body de /pieces que fija solo el IMEI del financiado; `undefined` si no se rastrea por IMEI. */
export function financedImeiPinBody(
    session: WizardSession | undefined,
    imei: string,
): PinPiecesLine[] | undefined {
    const line = financedLineOf(session);
    const phone = financedImeiUnit(line);
    if (!line || !phone) return undefined;
    return [{ lineId: line.lineId, components: [{ sku: phone.sku, identifiers: [imei] }] }];
}

/** IMEI del teléfono financiado ya fijado en la sesión; vacío si no hay. */
export function pinnedFinancedImei(session: WizardSession | undefined): string {
    const line = financedLineOf(session);
    if (!line) return '';
    const sku = phoneSkuOf(line);
    return line.identifiers.find((i) => i.sku === sku)?.identifiers[0] ?? '';
}

/**
 * Body de /pieces: las piezas capturadas y, en crédito, el IMEI del financiado si su teléfono se
 * rastrea por IMEI. `undefined` si no hay nada que fijar (venta solo por cantidad).
 */
export function pinPiecesBody(
    session: WizardSession | undefined,
    pieces: DeliveryPiece[],
    captured: CapturedIdentifiers,
    financedImei?: string,
): PinPiecesLine[] | undefined {
    const line = financedLineOf(session);
    const phone = line ? unitsOf(line).find((u) => u.sku === phoneSkuOf(line)) : undefined;
    if (!line || !phone || phone.trackingMode !== 'IMEI' || !financedImei) {
        return completeSaleLinesOf(pieces, captured);
    }
    const financedPiece: DeliveryPiece = {
        key: FINANCED_KEY,
        lineId: line.lineId,
        sku: phone.sku,
        trackingMode: 'IMEI',
        label: phone.name,
    };
    return completeSaleLinesOf([financedPiece, ...pieces], {
        ...captured,
        [FINANCED_KEY]: financedImei,
    });
}
